import { Box, Tab, Tabs } from "@mui/material";
import React, { useState } from "react";
import { AdditionalNotesContainer } from "./AdditionalNotesContainer";
import { AmenitiesContainer } from "./AmenitiesContainer";
import { BuildingConditionContainer } from "./BuildingConditionContainer";
import { CommunityAndManagementContainer } from "./CommunityAndManagementContainer";
import { FinancialInformationContainer } from "./FinancialInformationContainer";
import { InsuranceSummaryContainer } from "./InsuranceSummaryContainer";
import { LocalAreaInsightsContainer } from "./LocalAreaInsightsContainer";
import { PropertyOverviewContainer } from "./PropertyOverviewContainer";
import { RulesAndRestrictionsContainer } from "./RulesAndRestrictionsContainer";
import { PropertyReportStatusIndicator } from "./PropertyReportStatusIndicator";
import { AIDisclaimer } from "../../../shared/components";

interface PropertyReportTabsProps {
  userId: string;
  propertyId: string;
}

function a11yProps(index: number) {
  return {
    id: `property-report-tab-${index}`,
    "aria-controls": `property-report-tabpanel-${index}`,
  };
}

export const PropertyReportTabs: React.FC<PropertyReportTabsProps> = ({
  userId,
  propertyId,
}) => {
  const [value, setValue] = useState(0);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const reportSections = [
    { label: "Property Overview", component: PropertyOverviewContainer },
    {
      label: "Financial Information",
      component: FinancialInformationContainer,
    },
    { label: "Building Condition", component: BuildingConditionContainer },
    { label: "Amenities", component: AmenitiesContainer },
    { label: "Rules & Restrictions", component: RulesAndRestrictionsContainer },
    { label: "Insurance Summary", component: InsuranceSummaryContainer },
    {
      label: "Community & Management",
      component: CommunityAndManagementContainer,
    },
    { label: "Local Area Insights", component: LocalAreaInsightsContainer },
    { label: "Additional Notes", component: AdditionalNotesContainer },
  ];

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", minHeight: 0 }}>
      {/* Left side - Status indicator above vertical tabs */}
      <Box
        sx={{
          width: "300px",
          borderRight: 1,
          borderColor: "divider",
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Report Status */}
        <Box sx={{ flexShrink: 0, mb: 2 }}>
          <PropertyReportStatusIndicator propertyId={propertyId} />
        </Box>

        {/* Vertical tabs */}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <Tabs
            value={value}
            onChange={handleChange}
            orientation="vertical"
            aria-label="property report sections"
            sx={{
              "& .MuiTab-root": {
                alignItems: "flex-start",
                textAlign: "left",
                minHeight: 40,
                px: 2,
                py: 1,
              },
            }}
          >
            {reportSections.map((section, index) => (
              <Tab
                key={index}
                label={section.label}
                {...a11yProps(index)}
                sx={{ maxWidth: "none", width: "100%" }}
              />
            ))}
          </Tabs>
        </Box>
      </Box>

      {/* Right side - Tab content */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          p: 1,
        }}
      >
        <AIDisclaimer />
        {reportSections.map((section, index) => {
          const Component = section.component;
          return (
            <Box
              key={index}
              role="tabpanel"
              hidden={value !== index}
              id={`property-report-tabpanel-${index}`}
              aria-labelledby={`property-report-tab-${index}`}
              sx={{
                display: value === index ? "block" : "none",
                flex: 1,
                minHeight: 0,
              }}
            >
              {value === index && (
                <Component userId={userId} propertyId={propertyId} />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
